import PostResponse from 'data/PostResponse';
import { CustomerAccountRegistrationResult } from 'features/registration/enums/customerAccountRegistrationResult';
import Alert from 'features/shared/alerts/components/Alert';
import { AlertType } from 'features/shared/alerts/enums/alertType';
import IContentDialogService from 'features/shared/components/contentDialog/IContentDialogService';
import ActionResultDTO from 'features/shared/dtos/ActionResultDTO';
import { TFunction } from 'i18next';
import { useInjection } from 'infrastructure/ioc/ioc.react';
import { IocTypes } from 'infrastructure/ioc/ioc.types';
import React from 'react';
import { getPortalRootUrl, getResetPasswordUrl } from 'features/shared/helpers/urlHelper';
import { useTranslation } from 'react-i18next';

interface Props {
  result: PostResponse<ActionResultDTO<CustomerAccountRegistrationResult>>;
  regResultTranslate: TFunction;
  onLoginPageClick: () => void;
  onAddtionalFormClick: () => void;
};

const RegistrationResultSubDialog = ({ result, regResultTranslate, onLoginPageClick, onAddtionalFormClick }: Props) => {
  // translation
  const tLabels = useTranslation("registration\\registration").t;

  // DI
  const dialogService = useInjection<IContentDialogService>(IocTypes.ContentDialogService);

  // private props
  const _errorMoreInfoMessage: string = tLabels("registrationResultSubDialog.errorMoreInfoMessage");
  const _resultToAlertMappings: Record<CustomerAccountRegistrationResult, AlertType>  = {
    AccountCreated: "Success",
    AccountExists: "Warning",
    AccountExistsAndLinked: "Success",
    CustomerNotFound: "Warning",
    CustomerNotFoundSended: "Success",
    Undefined: "Danger"
  };

  // helpers
  const isSuccessStatus = (): boolean => {
    return result.status < 300;
  };

  const getRegistrationResult = (): CustomerAccountRegistrationResult => {
    if (result.data?.result) {
      return result.data.result;
    }    

    return "Undefined";
  };

  // sub-renders
  const renderAlert = () => {
    let alertType: AlertType;
    let message: string;

    if (isSuccessStatus()) {
      alertType = _resultToAlertMappings[result.data?.result!]
      message = regResultTranslate(`${result.data?.result}.full`, {
        portalUrl: getPortalRootUrl()
      });
    } else {
      alertType = "Danger";
      message =_errorMoreInfoMessage;
    }

    return <Alert alertObj={{alertType: alertType, message: message }} className="m-0" messageClassName="w-100 text-break" />    
  };

  const renderActionButtons = () => {
    if (isSuccessStatus()) {
      let status = getRegistrationResult();

      switch (status) {
        case "AccountCreated":
        case "AccountExistsAndLinked":
          return renderLoginButton();
        case "AccountExists":
          return (
            <>
              {renderResetPasswordButton()}
              {renderLoginButton()}
            </>
          ); 
        case "CustomerNotFound":
          return (
            <>
              {renderCheckFormButton("btn-outline-secondary")}
              <button className="btn btn-primary ml-2" onClick={onAddtionalFormClick}>
                {tLabels("registrationResultSubDialog.addData")}
              </button>
            </>
          );
        case "CustomerNotFoundSended":
          return (
            <button className="btn btn-primary ml-2" onClick={onLoginPageClick}>
              {tLabels("registrationResultSubDialog.close")}
            </button>
          );
      }
    } 
    // error status code or Undefined status
    return renderCheckFormButton();
  }

  const renderLoginButton = () => { 
    return (
      <button className="btn btn-primary ml-2" onClick={onLoginPageClick}>
        {tLabels("registrationResultSubDialog.login")}
      </button>
    );
  }

  const renderResetPasswordButton = () => {
    return (
      <a className="btn btn-outline-secondary" href={getResetPasswordUrl()}>
        {tLabels("registrationResultSubDialog.forgotPassword")}
      </a>
    );     
  };

  const renderCheckFormButton = (btnClass: string = "btn-primary") => { 
    return (
      <button className={`btn ${btnClass} ml-2`} onClick={() => dialogService.closeDialog()}>
        {tLabels("registrationResultSubDialog.checkForm")}
      </button>
    );
  }

  // render
  return (
    <>
      <div className="p-4">
        {renderAlert()}
      </div>
      <div className="d-flex justify-content-end px-4 pb-4">
        {renderActionButtons()}
      </div>
    </>
  );
};

export default RegistrationResultSubDialog;
